import {
  type MirrorCache,
  MirrorPool,
  normalizeStations,
  type QueryParams,
  type RadioStation,
} from "@agency/shared";
import { STORAGE_KEYS } from "../config.js";

const PAGE_SIZE = 100;
const MAX_OFFSET = 200;
/** Keep paging until this many stations survive normalization. */
const TARGET_ELIGIBLE = 25;
const POPULAR_TTL_MS = 15 * 60 * 1000;

const BASE_QUERY: QueryParams = {
  hidebroken: true,
  is_https: true,
  order: "clickcount",
  reverse: true,
  limit: PAGE_SIZE,
};

const mirrorCache: MirrorCache = {
  load() {
    const raw = localStorage.getItem(STORAGE_KEYS.radioMirrors);
    return raw ? (JSON.parse(raw) as { hosts: string[]; savedAt: number }) : null;
  },
  save(hosts, savedAt) {
    localStorage.setItem(STORAGE_KEYS.radioMirrors, JSON.stringify({ hosts, savedAt }));
  },
};

/**
 * Station discovery straight from the browser (Radio Browser serves CORS).
 * Throws `MirrorError` when no mirror answers.
 */
export class RadioDirectoryClient {
  private readonly pool = new MirrorPool({ timeoutMs: 6000, maxMirrors: 3, cache: mirrorCache });
  private popular?: { at: number; stations: RadioStation[] };

  /** The most-clicked eligible stations, cached for a while. */
  async listPopular(signal?: AbortSignal, refresh = false): Promise<RadioStation[]> {
    if (!refresh && this.popular && Date.now() - this.popular.at < POPULAR_TTL_MS) {
      return this.popular.stations;
    }
    const stations = await this.searchPaged({}, signal);
    this.popular = { at: Date.now(), stations };
    return stations;
  }

  /** Name search; an empty query is the popular list. */
  async searchByName(name: string, signal?: AbortSignal): Promise<RadioStation[]> {
    const query = name.trim();
    if (!query) return this.listPopular(signal);
    return this.searchPaged({ name: query }, signal);
  }

  /** Count one listen, as Radio Browser asks. Never blocks or breaks playback. */
  async reportPlay(stationId: string): Promise<void> {
    try {
      await this.pool.getJson(`/json/url/${encodeURIComponent(stationId)}`);
    } catch {
      // Non-fatal by design.
    }
  }

  /** Page through results until enough stations pass the local filters. */
  private async searchPaged(params: QueryParams, signal?: AbortSignal): Promise<RadioStation[]> {
    const seen = new Set<string>();
    const out: RadioStation[] = [];
    for (let offset = 0; offset <= MAX_OFFSET; offset += PAGE_SIZE) {
      const body = await this.pool.getJson(
        "/json/stations/search",
        { ...BASE_QUERY, ...params, offset },
        signal,
      );
      out.push(...normalizeStations(body, seen));
      const exhausted = !Array.isArray(body) || body.length < PAGE_SIZE;
      if (out.length >= TARGET_ELIGIBLE || exhausted) break;
    }
    return out;
  }
}
